import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Select,
  MenuItem,
  InputLabel,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  PhotoCamera,
  Delete,
  Download,
  AccessTime
} from '@mui/icons-material';
import api from '../../services/api';
import { brand } from '../../theme/colors';

const ClientDeliveryConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [confirmationData, setConfirmationData] = useState({
    allItemsReceived: true,
    itemsReceived: [],
    clientPhotos: [],
    clientComments: ''
  });
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('poor_quality');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const DISPUTE_REASONS = [
    { value: 'poor_quality', label: 'Poor quality / damaged' },
    { value: 'work_not_completed', label: 'Items not delivered / missing' },
    { value: 'payment_issue', label: 'Payment / pricing issue' },
    { value: 'timeline_breach', label: 'Delivery was late' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  useEffect(() => {
    if (order?.auto_release_scheduled_at && !order.delivery_confirmed_by_client) {
      calculateTimeRemaining();
      const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [order]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');

      const orderResponse = await api.get(`/material-orders/${orderId}`);

      if (orderResponse.data.success) {
        setOrder(orderResponse.data.data);

        // Load delivery details
        const deliveryResponse = await api.get(`/material-orders/${orderId}/delivery`);

        if (deliveryResponse.data.success) {
          setDelivery(deliveryResponse.data.data);
        }

        // Initialize items received data
        const items = orderResponse.data.data.items || [];
        setConfirmationData(prev => ({
          ...prev,
          itemsReceived: items.map(item => ({
            item_id: item.id,
            quantity_delivered: item.quantity,
            condition: 'good',
            notes: ''
          }))
        }));
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeRemaining = () => {
    if (!order?.auto_release_scheduled_at) return;

    const autoReleaseDate = new Date(order.auto_release_scheduled_at);
    const now = new Date();
    const remaining = autoReleaseDate - now;

    if (remaining > 0) {
      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setTimeRemaining(`${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`);
      } else {
        setTimeRemaining(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
      }
    } else {
      setTimeRemaining('Auto-release scheduled');
    }
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    // TODO: Upload photos to server
    setConfirmationData(prev => ({
      ...prev,
      clientPhotos: [...prev.clientPhotos, ...files.map(f => URL.createObjectURL(f))]
    }));
  };

  const removePhoto = (index) => {
    setConfirmationData(prev => ({
      ...prev,
      clientPhotos: prev.clientPhotos.filter((_, i) => i !== index)
    }));
  };

  const updateItemConfirmation = (itemId, field, value) => {
    setConfirmationData(prev => ({
      ...prev,
      itemsReceived: prev.itemsReceived.map(item =>
        item.item_id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleConfirmDelivery = async () => {
    try {
      setConfirming(true);
      setError('');

      const response = await api.post(`/material-orders/${orderId}/confirm`, confirmationData);

      if (response.data.success) {
        // Success - show success message and redirect
        setTimeout(() => {
          navigate(`/projects/${order?.project_id}`);
        }, 2000);
      } else {
        setError(response.data.error || 'Failed to confirm delivery');
      }
    } catch (err) {
      console.error('Error confirming delivery:', err);
      setError(err.response?.data?.error || 'Failed to confirm delivery. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleReportIssue = async () => {
    if (!disputeReason || disputeDescription.trim().length < 5) {
      return;
    }

    try {
      setSubmittingDispute(true);

      const response = await api.post(`/material-orders/${orderId}/dispute`, {
        reason: disputeReason,
        description: disputeDescription.trim()
      });

      if (response.data.success) {
        setDisputeDialogOpen(false);
        setDisputeDescription('');
        setDisputeReason('poor_quality');
        setSnackbar({
          open: true,
          message: 'Issue reported — our team will review it',
          severity: 'success'
        });
        // Refresh order/delivery state to reflect the dispute
        loadOrder();
      } else {
        setSnackbar({
          open: true,
          message: response.data.error || 'Failed to report issue',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error reporting issue:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to report issue',
        severity: 'error'
      });
    } finally {
      setSubmittingDispute(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!order || !delivery) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Order or delivery information not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Confirm Material Delivery
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Order Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Order ID:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{order.id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Supplier:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{order.merchant_name}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Delivery Date:</Typography>
            <Typography variant="body1">{delivery.delivery_date}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              ${parseFloat(order.total_amount || 0).toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          🚚 Delivery Status
        </Typography>
        <Typography variant="body2">
          The supplier has marked this order as DELIVERED on {delivery.delivery_date} at {delivery.delivery_time || 'N/A'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          ⚠️ IMPORTANT: Please inspect all materials before confirming
        </Typography>
      </Alert>

      {/* Order Items Verification */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Order Items - Verify Delivery
        </Typography>

        {order.items?.map((item) => {
          const itemConfirmation = confirmationData.itemsReceived.find(
            ic => ic.item_id === item.id
          ) || { item_id: item.id, quantity_delivered: item.quantity, condition: 'good', notes: '' };

          return (
            <Card key={item.id} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.material_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ordered: {item.quantity} {item.unit}
                  </Typography>
                </Box>
                <Chip
                  label={itemConfirmation.quantity_delivered === item.quantity ? '✓ Matches order' : 'Quantity mismatch'}
                  color={itemConfirmation.quantity_delivered === item.quantity ? 'success' : 'warning'}
                  size="small"
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Quantity Delivered"
                    type="number"
                    value={itemConfirmation.quantity_delivered}
                    onChange={(e) => updateItemConfirmation(item.id, 'quantity_delivered', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Condition:</FormLabel>
                    <RadioGroup
                      row
                      value={itemConfirmation.condition}
                      onChange={(e) => updateItemConfirmation(item.id, 'condition', e.target.value)}
                    >
                      <FormControlLabel value="good" control={<Radio />} label="Good" />
                      <FormControlLabel value="damaged" control={<Radio />} label="Damaged" />
                      <FormControlLabel value="wrong_item" control={<Radio />} label="Wrong Item" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes (Optional)"
                    multiline
                    rows={2}
                    value={itemConfirmation.notes}
                    onChange={(e) => updateItemConfirmation(item.id, 'notes', e.target.value)}
                    placeholder="E.g., 'All bags intact, fresh stock'"
                  />
                </Grid>
              </Grid>
            </Card>
          );
        })}
      </Paper>

      {/* Supplier's Delivery Proof */}
      {delivery.delivery_proof_photos && delivery.delivery_proof_photos.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Supplier's Delivery Proof
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The supplier uploaded the following as proof of delivery:
          </Typography>
          <Grid container spacing={2}>
            {delivery.delivery_proof_photos.map((photoUrl, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={photoUrl}
                    alt={`Delivery proof ${index + 1}`}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
          {delivery.delivery_note_url && (
            <Button
              startIcon={<Download />}
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => window.open(delivery.delivery_note_url, '_blank')}
            >
              Download Delivery Note
            </Button>
          )}
        </Paper>
      )}

      {/* Client Photos */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Upload Your Photos (Optional but recommended)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Take photos of delivered materials for your records:
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {confirmationData.clientPhotos.map((photo, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={photo}
                  alt={`Client photo ${index + 1}`}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <IconButton size="small" color="error" onClick={() => removePhoto(index)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
              onClick={() => document.getElementById('client-photo-upload').click()}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Add Photo
                </Typography>
              </Box>
              <input
                id="client-photo-upload"
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
              />
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Delivery Confirmation */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Your Delivery Confirmation
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            Did you receive all items in good condition? *
          </FormLabel>
          <RadioGroup
            value={confirmationData.allItemsReceived}
            onChange={(e) => setConfirmationData({
              ...confirmationData,
              allItemsReceived: e.target.value === 'true'
            })}
          >
            <FormControlLabel
              value={true}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">YES - Everything delivered as ordered</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Payment of ${parseFloat(order.total_amount || 0).toFixed(2)} will be released to supplier
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={false}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">NO - Major issues with delivery</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dispute will be raised, payment held pending resolution
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Additional Comments (Optional)"
          value={confirmationData.clientComments}
          onChange={(e) => setConfirmationData({ ...confirmationData, clientComments: e.target.value })}
          sx={{ mt: 2 }}
          placeholder="Delivery was on time, materials look good quality. Driver was professional..."
        />
      </Paper>

      {/* Auto-Release Timer */}
      {timeRemaining && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'warning.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccessTime />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              ⏰ AUTO-RELEASE TIMER
            </Typography>
          </Box>
          <Typography variant="body2">
            If you don't confirm delivery within 72 business hours, payment will be automatically released to the supplier.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            Time Remaining: {timeRemaining}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            💡 Confirming now helps suppliers get paid faster and builds trust in the platform.
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={() => navigate(-1)} variant="outlined" disabled={confirming}>
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDisputeDialogOpen(true)}
            disabled={confirming}
          >
            Report Issue
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelivery}
            disabled={confirming}
            startIcon={confirming ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            {confirming ? 'Confirming...' : 'CONFIRM DELIVERY & RELEASE PAYMENT'}
          </Button>
        </Box>
      </Box>

      {/* Report Issue (Dispute) Dialog */}
      <Dialog
        open={disputeDialogOpen}
        onClose={() => !submittingDispute && setDisputeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report an issue</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Let us know what went wrong with this order. Raising an issue holds
            payment while our team reviews it.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="dispute-reason-label">Reason</InputLabel>
            <Select
              labelId="dispute-reason-label"
              label="Reason"
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              disabled={submittingDispute}
            >
              {DISPUTE_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            required
            label="Describe the issue"
            value={disputeDescription}
            onChange={(e) => setDisputeDescription(e.target.value)}
            disabled={submittingDispute}
            placeholder="Tell us what happened (at least a few words)..."
            helperText={
              disputeDescription.trim().length > 0 &&
              disputeDescription.trim().length < 5
                ? 'Please provide at least 5 characters'
                : ' '
            }
            error={
              disputeDescription.trim().length > 0 &&
              disputeDescription.trim().length < 5
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisputeDialogOpen(false)}
            disabled={submittingDispute}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReportIssue}
            disabled={
              submittingDispute ||
              !disputeReason ||
              disputeDescription.trim().length < 5
            }
            startIcon={submittingDispute ? <CircularProgress size={20} /> : null}
          >
            {submittingDispute ? 'Reporting...' : 'Report Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientDeliveryConfirmation;

